/** @jsx jsx */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { KeyboardEvent } from 'react';
import type { DayPickerProps } from 'react-day-picker';
import DayPicker from 'react-day-picker';
import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  isSameMonth,
  setDate,
} from 'date-fns';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import type { BalanceThemeRaw } from '@balance-web/theme';
import { useRawTheme } from '@balance-web/theme';
import { getContrastText, hexToRgb, useClickOutside } from '@balance-web/utils';
import { buildDataAttributes } from '@balance-web/core';
import type { WithDataAttributeProp } from '@balance-web/core';

import { CalendarCaption } from './CalendarCaption';
import { CalendarViewContext } from './CalendarViewContext';
import { CalendarPeriodView } from './CalendarPeriodView';
import type { CalendarPeriodFrame } from './CalendarPeriodView';
import { getArrowKeyStep } from './calendarPeriods';
import type { CalendarPeriod } from './calendarPeriods';
import {
  getAdjacentMonthViews,
  getPanelBounds,
  isMonthVisible,
  moveMonthViewWithinBounds,
  setMonthView,
  shiftMonthViews,
  shiftWithinBounds,
} from './calendarMonthViews';
import type { MonthView } from './calendarMonthViews';

export type CalendarProps = WithDataAttributeProp<DayPickerProps>;

type MonthPlacement = { monthIndex: number; frame: CalendarPeriodFrame };

type PeriodView = MonthPlacement & { period: CalendarPeriod; anchor: Date };

const PANEL_SELECTOR = '.DayPicker';
const MONTH_SELECTOR = '.DayPicker-Month';
const DAY_SELECTOR = '.DayPicker-Day:not(.DayPicker-Day--outside)';
const DAYS_PER_WEEK = 7;
const hiddenStyle = { visibility: 'hidden' } as const;

function getHiddenPanelStyles(monthIndex: number) {
  return {
    '.DayPicker-NavBar': hiddenStyle,
    [`.DayPicker:nth-of-type(${monthIndex + 1})`]: hiddenStyle,
  };
}

function measureMonth(trigger: HTMLElement): MonthPlacement | null {
  const calendar = trigger.closest('[data-balance-component="calendar"]');
  const panel = trigger.closest(PANEL_SELECTOR);
  const month = trigger.closest(MONTH_SELECTOR);
  if (!calendar || !panel || !month) {
    return null;
  }

  const calendarRect = calendar.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const monthRect = month.getBoundingClientRect();
  return {
    monthIndex: Array.from(calendar.querySelectorAll(MONTH_SELECTOR)).indexOf(
      month
    ),
    frame: {
      left: monthRect.left - calendarRect.left,
      top: panelRect.top - calendarRect.top,
      width: monthRect.width,
      height: panelRect.height,
    },
  };
}

function getPanels(calendar: HTMLElement) {
  return Array.from(calendar.querySelectorAll(PANEL_SELECTOR));
}

function findDayNode(calendar: HTMLElement, views: MonthView[], day: Date) {
  const panelIndex = views.findIndex((view) => isSameMonth(view.month, day));
  return getPanels(calendar)[panelIndex]?.querySelectorAll<HTMLElement>(
    DAY_SELECTOR
  )[day.getDate() - 1];
}

function getFocusedDay(
  calendar: HTMLElement,
  views: MonthView[],
  target: EventTarget
) {
  if (!(target instanceof HTMLElement) || !target.matches(DAY_SELECTOR)) {
    return null;
  }

  const panel = target.closest(PANEL_SELECTOR);
  const view = panel ? views[getPanels(calendar).indexOf(panel)] : undefined;
  if (!panel || !view) {
    return null;
  }

  const dayIndex = Array.from(panel.querySelectorAll(DAY_SELECTOR)).indexOf(
    target
  );
  return { view, day: setDate(view.month, dayIndex + 1) };
}

export const Calendar = forwardRef<DayPicker, CalendarProps>(
  (
    {
      modifiers,
      data,
      month,
      initialMonth,
      numberOfMonths = 1,
      fromMonth,
      toMonth,
      canChangeMonth = true,
      onMonthChange,
      ...props
    },
    ref
  ) => {
    const rawTheme = useRawTheme();
    const styles = useMemo(() => {
      return getCalendarStyles(rawTheme);
    }, [rawTheme]);
    const indexOfMonday = 1;
    const augmentedModifiers = useMemo(() => {
      return {
        ...modifiers,
        weekend: { daysOfWeek: [0, 6] },
      };
    }, [modifiers]);
    const testid = getTestIdFromDataAttributes(data);
    const dataAttributes = buildDataAttributes(data);

    const bounds = { fromMonth, toMonth };
    const [monthViews, setMonthViews] = useState(() =>
      getAdjacentMonthViews(
        month ?? initialMonth ?? new Date(),
        numberOfMonths,
        bounds
      )
    );
    const [periodView, setPeriodView] = useState<PeriodView | null>(null);
    const [syncedMonth, setSyncedMonth] = useState(month);
    if (syncedMonth !== month) {
      setSyncedMonth(month);
      const requestedMonth = month ?? initialMonth ?? new Date();
      if (!isMonthVisible(monthViews, requestedMonth)) {
        setMonthViews(
          getAdjacentMonthViews(requestedMonth, numberOfMonths, bounds)
        );
      }
      setPeriodView(null);
    }

    const firstView = monthViews[0];
    const lastIndex = monthViews.length - 1;
    const lastView = monthViews[lastIndex];
    const navigationFromMonth =
      fromMonth && firstView && lastView
        ? addMonths(
            fromMonth,
            differenceInCalendarMonths(lastView.month, firstView.month)
          )
        : fromMonth;

    const updateMonthViews = (nextViews: MonthView[]) => {
      setMonthViews(nextViews);
      const nextFirstMonth = nextViews[0]?.month;
      if (
        nextFirstMonth &&
        firstView &&
        differenceInCalendarMonths(nextFirstMonth, firstView.month) !== 0
      ) {
        onMonthChange?.(nextFirstMonth);
      }
    };

    const handleNavigation = (nextMonth: Date) => {
      if (lastView) {
        updateMonthViews(
          shiftMonthViews(
            monthViews,
            differenceInCalendarMonths(nextMonth, lastView.month)
          )
        );
      }
    };

    const wrapperRef = useRef<HTMLDivElement>(null);
    const [
      periodViewElement,
      setPeriodViewElement,
    ] = useState<HTMLDivElement | null>(null);
    const pendingCaptionFocus = useRef<{
      period: CalendarPeriod;
      offset: number;
    } | null>(null);
    const pendingDayFocus = useRef<Date | null>(null);

    const openPeriodView = useCallback(
      (period: CalendarPeriod, anchor: Date, trigger: HTMLElement) => {
        const placement = measureMonth(trigger);
        if (placement) {
          setPeriodView({ period, anchor, ...placement });
        }
      },
      []
    );
    const viewContext = useMemo(() => ({ openPeriodView }), [openPeriodView]);

    const closePeriodView = useCallback(() => {
      setPeriodView(null);
    }, []);
    const clickOutsideElements = useMemo(() => [periodViewElement], [
      periodViewElement,
    ]);
    useClickOutside({
      handler: closePeriodView,
      elements: clickOutsideElements,
      listenWhen: periodView !== null,
    });

    useEffect(() => {
      const pending = pendingCaptionFocus.current;
      const wrapper = wrapperRef.current;
      if (periodView !== null || !pending || !wrapper) {
        return;
      }

      pendingCaptionFocus.current = null;
      const captionButtons = wrapper.querySelectorAll<HTMLButtonElement>(
        `[data-calendar-period="${pending.period}"]`
      );
      (captionButtons[pending.offset] ?? captionButtons[0])?.focus();
    }, [periodView]);

    useEffect(() => {
      const day = pendingDayFocus.current;
      const wrapper = wrapperRef.current;
      if (!day || !wrapper) {
        return;
      }

      pendingDayFocus.current = null;
      // react-day-picker v7 applies a new month prop in componentDidUpdate, so its new days render after this effect.
      const frame = requestAnimationFrame(() => {
        findDayNode(wrapper, monthViews, day)?.focus();
      });
      return () => {
        cancelAnimationFrame(frame);
      };
    }, [monthViews]);

    const moveDayFocus = (event: KeyboardEvent<HTMLDivElement>) => {
      const step = getArrowKeyStep(event.key, DAYS_PER_WEEK);
      if (step === undefined) {
        return;
      }

      const focused = getFocusedDay(
        event.currentTarget,
        monthViews,
        event.target
      );
      if (!focused) {
        return;
      }

      const nextDay = addDays(focused.day, step);
      if (isSameMonth(nextDay, focused.view.month)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (isMonthVisible(monthViews, nextDay)) {
        findDayNode(event.currentTarget, monthViews, nextDay)?.focus();
        return;
      }

      const nextViews = canChangeMonth
        ? shiftWithinBounds(
            monthViews,
            differenceInCalendarMonths(nextDay, focused.view.month),
            bounds
          ) ??
          moveMonthViewWithinBounds(
            monthViews,
            monthViews.indexOf(focused.view),
            nextDay,
            bounds
          )
        : null;
      if (nextViews) {
        pendingDayFocus.current = nextDay;
        updateMonthViews(nextViews);
      }
    };

    const returnToDays = ({ period, monthIndex }: PeriodView) => {
      pendingCaptionFocus.current = { period, offset: monthIndex };
      setPeriodView(null);
    };

    const selectPeriodMonth = (view: PeriodView, selectedMonth: Date) => {
      updateMonthViews(
        setMonthView(monthViews, view.monthIndex, selectedMonth)
      );
      if (view.period === 'year') {
        setPeriodView({ ...view, period: 'month', anchor: selectedMonth });
      } else {
        returnToDays(view);
      }
    };

    return (
      <CalendarViewContext.Provider value={canChangeMonth ? viewContext : null}>
        <div
          ref={wrapperRef}
          css={[
            styles,
            periodView && getHiddenPanelStyles(periodView.monthIndex),
          ]}
          data-balance-component="calendar"
          data-testid={testid}
          onKeyDownCapture={moveDayFocus}
        >
          {monthViews.map((view, index) => {
            const isNavigationPanel = index === lastIndex;
            return (
              <DayPicker
                key={view.panel}
                ref={index === 0 ? ref : undefined}
                captionElement={CalendarCaption}
                firstDayOfWeek={indexOfMonday}
                modifiers={augmentedModifiers}
                {...dataAttributes}
                {...props}
                canChangeMonth={canChangeMonth && isNavigationPanel}
                fromMonth={isNavigationPanel ? navigationFromMonth : undefined}
                month={view.month}
                toMonth={isNavigationPanel ? toMonth : undefined}
                onMonthChange={handleNavigation}
              />
            );
          })}
          {periodView ? (
            <CalendarPeriodView
              key={periodView.period}
              ref={setPeriodViewElement}
              anchor={periodView.anchor}
              frame={periodView.frame}
              period={periodView.period}
              {...getPanelBounds(
                bounds,
                periodView.monthIndex,
                monthViews.length
              )}
              onCancel={() => {
                returnToDays(periodView);
              }}
              onPeriodClick={(period) => {
                if (period === periodView.period) {
                  returnToDays(periodView);
                } else {
                  setPeriodView({ ...periodView, period });
                }
              }}
              onSelect={(selectedMonth) => {
                selectPeriodMonth(periodView, selectedMonth);
              }}
            />
          ) : null}
        </div>
      </CalendarViewContext.Provider>
    );
  }
);

// Styles
// ------------------------------

function getCalendarStyles(theme: BalanceThemeRaw) {
  const cellSize = theme.sizing.base;
  const navButtonSize = theme.sizing.xsmall;
  const interactionColor = theme.semantic.colour.primary.background;
  const rangeBetweenColor = hexToRgb(interactionColor, 0.2);

  return {
    color: theme.semantic.colour.text.textPrimary,
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing.small,
    position: 'relative',

    // resets and wrapper stuff
    '.DayPicker': {
      display: 'inline-block',
      fontSize: '1rem',
    },
    '.DayPicker-wrapper': {
      position: 'relative',
      flexDirection: 'row',
      userSelect: 'none',
      outline: 0,
    },
    '.DayPicker-Months': {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    '.DayPicker-Month': {
      display: 'table',

      // separate weeks for easier parsing of range selection
      borderSpacing: '0 2px',
      borderCollapse: 'separate',

      // separate months for easier parsing of range selection
      margin: theme.spacing.small,

      // NOTE: resolve weird safari bug:
      // https://bugs.webkit.org/show_bug.cgi?id=187903
      position: 'relative',
      '.DayPicker-Caption > div': { position: 'absolute' },
    },

    // the caption is the day/month title e.g. "July 2020"
    '.DayPicker-Caption': {
      display: 'table-caption',
      height: navButtonSize,
      marginBottom: '0.5em',
      padding: 0,
      textAlign: 'left',
    },
    '.DayPicker-Caption > div': {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing.xsmall,
      fontWeight: theme.typography.fontWeight.medium,
      fontSize: theme.typography.fontSize.medium,
      height: navButtonSize,
    },

    // weekdays
    '.DayPicker-Weekdays': { display: 'table-header-group', marginTop: '1em' },
    '.DayPicker-WeekdaysRow': { display: 'table-row' },
    '.DayPicker-Weekday': {
      color: theme.semantic.colour.text.textSecondary,
      display: 'table-cell',
      fontSize: theme.typography.fontSize.small,
      fontWeight: theme.typography.fontWeight.medium,
      padding: '0.5em',
      textAlign: 'center',
    },
    '.DayPicker-Weekday abbr[title]': {
      borderBottom: 'none',
      textDecoration: 'none',
    },
    '.DayPicker-Body': {
      display: 'table-row-group',
      fontSize: theme.typography.fontSize.small,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '.DayPicker-Week': { display: 'table-row' },
    '.DayPicker-WeekNumber': {
      display: 'table-cell',
      padding: '0.5em',
      minWidth: '1em',
      borderRight: '1px solid #EAECEC',
      color: theme.semantic.colour.text.textSecondary,
      verticalAlign: 'middle',
      textAlign: 'right',
      fontSize: '0.75em',
      cursor: 'pointer',
    },
    '.DayPicker--interactionDisabled .DayPicker-Day': { cursor: 'default' },

    // nav buttons
    '.DayPicker-NavBar': {
      display: 'flex',
      position: 'absolute',
      right: theme.spacing.xsmall,
      top: theme.spacing.xsmall,
      zIndex: 1,
    },
    '.DayPicker-NavButton': {
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      // backgroundSize: '66.667%',
      borderRadius: theme.radii.xsmall,
      color: theme.semantic.colour.text.textPrimary,
      cursor: 'pointer',
      display: 'inline-block',
      height: theme.sizing.small,
      left: 'auto',
      width: theme.sizing.small,

      ':hover, &:focus-visible': {
        backgroundColor:
          theme.semantic.colour.neutral.container.interactions.hover,
        color: theme.semantic.colour.text.textPrimary,
        outline: 0,
      },
      ':active': {
        backgroundColor:
          theme.semantic.colour.neutral.container.interactions.pressed,
        color: theme.semantic.colour.text.textPrimary,
      },
    },
    '.DayPicker-NavButton--next': {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24' stroke='${encodeURIComponent(
        theme.semantic.colour.text.textPrimary
      )}' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round' %3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E")`,
    },
    '.DayPicker-NavButton--prev': {
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24' stroke='${encodeURIComponent(
        theme.semantic.colour.text.textPrimary
      )}' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round' %3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E")`,
    },

    // "day" or grid cell
    '.DayPicker-Day--outside': {
      color: theme.semantic.colour.text.textSecondary,
      cursor: 'default',
    },
    '.DayPicker-Day--weekend': {
      color: theme.semantic.colour.text.textSecondary,
    },
    '.DayPicker-Day--disabled': {
      color: theme.semantic.colour.text.textDisabled,
      cursor: 'default',
    },

    '.DayPicker-Day': {
      borderRadius: '50%',
      display: 'table-cell',
      height: cellSize,
      outline: 0, // we handle focus below, with box-shadow
      padding: 0,
      position: 'relative',
      textAlign: 'center',
      verticalAlign: 'middle',
      width: cellSize,
    },
    '.DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside)': {
      cursor: 'pointer',

      '&:hover, &:focus-visible': {
        // backgroundColor: 'transparent',
        outline: 0,

        '&::after': {
          borderRadius: '50%',
          boxShadow: `inset 0 0 0 2px ${interactionColor}`,
          content: '" "',
          height: cellSize,
          left: 0,
          position: 'absolute',
          top: 0,
          width: cellSize,
        },
      },
    },
    '.DayPicker-Day--today': {
      color: theme.semantic.colour.text.reserved.critical,
      fontWeight: theme.typography.fontWeight.bold,
    },
    '.DayPicker-Day--selected:not(.DayPicker-Day--outside)': {
      color: getContrastText(interactionColor),

      '&, &:hover, &:focus-visible': {
        backgroundColor: interactionColor,
      },
    },

    // range-specific day styles
    '.DayPicker-Day--rangeStart:not(.DayPicker-Day--outside), .DayPicker-Day--rangeEnd:not(.DayPicker-Day--outside)': {
      '&::before': {
        backgroundColor: rangeBetweenColor,
        position: 'absolute',
        content: '" "',
        width: cellSize / 2,
        height: cellSize,
        top: 0,
        zIndex: -1,
      },
    },
    '.DayPicker-Day--rangeStart': {
      '&::before': {
        right: 0,
      },
    },
    '.DayPicker-Day--rangeEnd': {
      '&::before': {
        left: 0,
      },
    },
    '.DayPicker-Day--rangeBetween.DayPicker-Day--selected:not(.DayPicker-Day--outside)': {
      '&, &:hover, &:focus-visible': {
        backgroundColor: rangeBetweenColor,
        borderRadius: 0,
        color: theme.semantic.colour.text.textPrimary,
      },
    },
    '.DayPicker-Day--rangeBetween.DayPicker-Day--firstOfMonth:not(.DayPicker-Day--outside)': {
      '&, &:hover, &:focus-visible': {
        background: `linear-gradient(to left, ${rangeBetweenColor}, ${theme.semantic.colour.surface.neutral.background})`,
      },
    },
    '.DayPicker-Day--rangeBetween.DayPicker-Day--lastOfMonth:not(.DayPicker-Day--outside)': {
      '&, &:hover, &:focus-visible': {
        background: `linear-gradient(to right, ${rangeBetweenColor}, ${theme.semantic.colour.surface.neutral.background})`,
      },
    },
  } as const;
}
